import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { Button, Radio, Input, Slider, Space, message } from 'antd';
import { useBalance } from '@/app/hooks/user/mybalance';
import { View } from '../../../../../styles/reactnative';
import { ThemeContext } from '../../../../../theme/ThemeProvider';
import { useExchangeStore } from '../../../../hooks/trade/exchangeStore';
import BigNumber from 'bignumber.js';
import { calculateLiquidPriceNew } from '@/utils/ciutils';
import styled from 'styled-components';
import { t } from 'i18next';

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
  margin-bottom: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  .ant-radio-button-wrapper {
    height: 38px;
    line-height: 36px;
    text-align: center;
    background: ${(props) => props.theme.COLORS.bgColor};
    color: ${(props) => props.theme.COLORS.textw};
    border: 1px solid ${(props) => props.theme.COLORS.gray} !important;
    border-radius: 8px !important;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s ease;
    opacity: 0.6;

    &::before {
      display: none !important;
    }

    &:first-child {
      border-radius: 8px !important;
      border-inline-start: 1px solid ${(props) => props.theme.COLORS.gray} !important;
    }

    &:last-child {
      border-radius: 8px !important;
    }

    &:hover {
      opacity: 0.8;
    }

    &.ant-radio-button-wrapper-checked {
      background: ${(props) => props.theme.COLORS.primary} !important;
      color: #ffffff !important;
      border-color: ${(props) => props.theme.COLORS.primary} !important;
      box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25);
      opacity: 1;
    }

    &:not(:first-child)::before {
      display: none !important;
    }
  }
`;

const AmountInput = styled(Input)`
  width: 100%;
  margin-bottom: 16px;
  height: 44px;
  background: ${(props) => props.theme.COLORS.bgColor};
  color: ${(props) => props.theme.COLORS.textw};
  border: 1px solid ${(props) => props.theme.COLORS.gray};
  border-radius: 6px;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;

  input {
    background: transparent;
    color: ${(props) => props.theme.COLORS.textw};
    font-size: 15px;
    font-weight: 500;
    text-align: left;

    &::placeholder {
      color: ${(props) => props.theme.COLORS.textw};
      opacity: 0.3;
      font-weight: 400;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .ant-input-suffix {
    color: ${(props) => props.theme.COLORS.textw};
    opacity: 0.5;
    font-size: 13px;
    font-weight: 500;
  }

  &:hover {
    border-color: ${(props) => props.theme.COLORS.primary};
  }

  &:focus,
  &.ant-input-affix-wrapper-focused {
    border-color: ${(props) => props.theme.COLORS.primary};
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 12px;
  width: 100%;
  margin-top: 20px;
  padding: 14px;
  background: transparent;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.COLORS.gray};
  box-sizing: border-box;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  color: ${(props) => props.theme.COLORS.textw};
  opacity: 0.6;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
`;

const InfoValue = styled.span`
  color: ${(props) => props.theme.COLORS.textw};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MaxAmountBadge = styled.div<{ $reduce?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: ${(props) => props.$reduce
    ? 'rgba(239, 68, 68, 0.06)'
    : 'rgba(34, 197, 94, 0.06)'};
  border: 1px solid ${(props) => props.$reduce
    ? 'rgba(239, 68, 68, 0.15)'
    : 'rgba(34, 197, 94, 0.15)'};
  border-radius: 8px;
  box-sizing: border-box;

  span:first-child {
    color: ${(props) => props.theme.COLORS.textw};
    opacity: 0.7;
    font-size: 12px;
    font-weight: 500;
  }

  span:last-child {
    color: ${(props) => props.$reduce
      ? '#ef4444'
      : '#22c55e'};
    font-size: 14px;
    font-weight: 700;
  }
`;

const SliderWrapper = styled.div<{ $reduce?: boolean }>`
  width: 100%;
  margin-bottom: 4px;

  .ant-slider-handle {
    &::after {
      box-shadow: none !important;
      background: ${(props) => props.$reduce ? '#ef4444' : '#22c55e'} !important;
      border: none !important;
      width: 16px !important;
      height: 16px !important;
      inset-inline-start: 0 !important;
      inset-block-start: 0 !important;
    }
  }
`;

const AddMargin = ({ 
  item, 
  amount, 
  setAmount, 
  onReduceModeChange = (isReduce) => {}, 
  onMaxReduceAmountChange = (amount) => {}
}) => {
  const { EPs } = useExchangeStore();
  const { data } = useBalance('USDT', 1);
  const { theme } = useContext(ThemeContext);

  const [EP, setEP] = useState(
    EPs?.find((item2: any) => +item2.id == +item?.exchange_pairs_id)
  );

  const [liquid, setLiquid] = useState<any>(0);
  const [sAmount, setSAmount] = useState(amount);
  const [reduce, setReduce] = useState(false);
  const [isDirectEditing, setIsDirectEditing] = useState(false);

  const avbalance = new BigNumber(data?.balance).minus(data?.lockbalance);

  // 초기 증거금 계산 (최초 마진)
  const initialMargin = useMemo(() => {
    // 포지션의 초기 증거금을 가져옵니다.
    // orders 배열의 첫 번째 요소가 초기 주문임
    const firstOrder = item?.orders?.[0];
    
    // orders가 있고 첫 번째 주문의 margin이 있는 경우
    if (firstOrder?.margin && firstOrder.margin > 0) {
      return new BigNumber(firstOrder.margin).toNumber();
    }
    
    // orders가 없거나 margin이 없는 경우 계산으로 구함
    // 초기 증거금 = 포지션 크기 * 진입가 / 레버리지
    const positionValue = new BigNumber(item?.amount || 0)
      .multipliedBy(item?.price || 0);
    const calculatedInitialMargin = positionValue
      .dividedBy(item?.leverage || 1)
      .toNumber();
    
    console.log('Initial margin calculation:', {
      firstOrderMargin: firstOrder?.margin,
      currentMargin: item?.margin,
      calculatedInitialMargin,
      positionValue: positionValue.toNumber(),
      amount: item?.amount,
      price: item?.price,
      leverage: item?.leverage
    });
    
    return calculatedInitialMargin;
  }, [item]);

  // 추가된 증거금 계산 (현재 마진 - 초기 마진)
  const additionalMargin = useMemo(() => {
    const currentMargin = new BigNumber(item?.margin || 0).toNumber();
    const additional = Math.max(0, currentMargin - initialMargin);
    
    console.log('Additional margin calculation:', {
      currentMargin,
      initialMargin,
      additional
    });
    
    return additional;
  }, [item?.margin, initialMargin]);

  const handleModeChange = useCallback(
    (e) => {
      const isReduce = e.target.value === 'reduce';
      setReduce(isReduce);
      // 모드 전환시 입력값 초기화
      setAmount('');
      setSAmount(0);
      // 부모 컴포넌트에 모드 변경 알림
      onReduceModeChange(isReduce);
    },
    [setAmount, onReduceModeChange]
  );

  const marks = {
    0: {
      style: {
        color: theme.COLORS.textw,
        opacity: 0.5,
        fontSize: '11px',
        fontWeight: 500
      },
      label: '0%'
    },
    25: {
      style: {
        color: theme.COLORS.textw,
        opacity: 0.5,
        fontSize: '11px',
        fontWeight: 500
      },
      label: '25%'
    },
    50: {
      style: {
        color: theme.COLORS.textw,
        opacity: 0.5,
        fontSize: '11px',
        fontWeight: 500
      },
      label: '50%'
    },
    75: {
      style: {
        color: theme.COLORS.textw,
        opacity: 0.5,
        fontSize: '11px',
        fontWeight: 500
      },
      label: '75%'
    },
    100: {
      style: {
        color: theme.COLORS.textw,
        opacity: 0.5,
        fontSize: '11px',
        fontWeight: 500
      },
      label: '100%'
    },
  };

  // 최대 감소 가능 금액 계산 (초기증거금은 보호, 추가증거금만 감소 가능)
  const maxReduceAmount = useMemo(() => {
    const currentMargin = new BigNumber(item?.margin || 0).toNumber();
    
    // 초기증거금은 반드시 유지되어야 함 (포지션 유지를 위한 필수 증거금)
    // 감소 가능한 금액 = 현재 증거금 - 초기증거금
    const maxReducibleAmount = Math.max(0, currentMargin - initialMargin);
    
    // 증거금 감소시에는 사용자 잔고 제한이 없음 (잔고가 증가하므로)
    // 오직 추가로 넣은 증거금만 감소 가능
    const maxAmount = maxReducibleAmount;
    
    console.log('Max reduce amount calculation:', {
      currentMargin,
      initialMargin,
      additionalMargin,
      maxReducibleAmount,
      finalMaxAmount: maxAmount,
      avbalance: avbalance.toNumber(),
      note: '증거금 감소시 잔고 제한 없음 - 추가증거금만 감소 가능'
    });
    
    // 부모 컴포넌트에 최대 감소 가능 금액 알림
    onMaxReduceAmountChange(maxAmount);
    return maxAmount;
  }, [additionalMargin, initialMargin, item?.margin, onMaxReduceAmountChange]);

  // 입력이 시작될 때 호출
  const handleInputFocus = useCallback(() => {
    setIsDirectEditing(true);
  }, []);

  // 입력이 끝날 때 호출
  const handleInputBlur = useCallback(() => {
    setIsDirectEditing(false);
    
    // 입력값이 없으면 0으로 설정
    if (!amount) {
      setAmount('0');
      setSAmount(0);
      return;
    }
    
    // 값이 범위를 벗어나는지 확인하여 제한
    const numValue = parseFloat(amount);
    
    // 감소 모드에서 음수가 아닌 값이 입력되었을 경우 음수로 변환
    if (reduce && numValue > 0) {
      const limitedValue = Math.max(-maxReduceAmount, -numValue);
      setAmount(limitedValue.toString());
      
      const sliderValue = maxReduceAmount > 0 
        ? (Math.abs(limitedValue) / maxReduceAmount) * 100
        : 0;
      setSAmount(Math.min(100, Math.max(0, sliderValue)));
      
      message.warning(t('reduceModeMustBeNegative'));
      return;
    }
    
    // 감소 모드에서 최대 감소 가능 금액을 초과했는지 확인
    if (reduce && Math.abs(numValue) > maxReduceAmount) {
      const limitedValue = -maxReduceAmount;
      setAmount(limitedValue.toString());
      setSAmount(100);
      
      message.warning(t('exceededMaxReduceAmount'));
      return;
    }
    
    // 증가 모드에서 최대 증가 가능 금액을 초과했는지 확인
    if (!reduce && numValue > avbalance.toNumber()) {
      const limitedValue = avbalance.toNumber();
      setAmount(limitedValue.toString());
      setSAmount(100);
      
      message.warning(t('exceededMaxIncreaseAmount'));
      return;
    }
  }, [reduce, amount, maxReduceAmount, avbalance, setAmount]);

  const handleAmountChange = useCallback((e) => {
    const inputValue = e.target.value;
    
    // 빈 입력 처리
    if (!inputValue) {
      setAmount('');
      setSAmount(0);
      return;
    }
    
    // 감소 모드에서는 -를 유지하고, 숫자와 소수점만 허용
    const validInput = reduce 
      ? /^-?\d*\.?\d{0,4}$/ 
      : /^\d*\.?\d{0,4}$/;
      
    if (!validInput.test(inputValue)) {
      return;
    }
    
    // 직접 편집 모드에서는 사용자 입력 그대로 유지
    if (isDirectEditing) {
      setAmount(inputValue);
      return;
    }
    
    const numValue = parseFloat(inputValue);
    
    // NaN 체크
    if (isNaN(numValue)) {
      setAmount('');
      setSAmount(0);
      return;
    }

    // 감소 모드일 때는 음수값으로, 그렇지 않으면 양수값으로 처리
    let limitedValue;
    if (reduce) {
      // 감소 모드: 입력값이 양수면 음수로 변환, 최대 감소 금액 제한
      limitedValue = numValue < 0 
        ? Math.max(-maxReduceAmount, numValue) 
        : Math.max(-maxReduceAmount, -numValue);
    } else {
      // 증가 모드: 입력값이 음수면 양수로 변환
      limitedValue = Math.max(0, numValue);
    }
    
    setAmount(limitedValue.toString());
    
    // 슬라이더 값 계산
    const baseAmount = reduce ? maxReduceAmount : avbalance.toNumber();
    const sliderValue = baseAmount > 0 
      ? (Math.abs(limitedValue) / baseAmount) * 100
      : 0;
      
    setSAmount(Math.min(100, Math.max(0, sliderValue)));
  }, [reduce, maxReduceAmount, avbalance, isDirectEditing, setAmount]);

  // Liquid Price 계산을 위한 useEffect
  useEffect(() => {
    if (sAmount === undefined || sAmount === null) return;

    const calculateLiquid = async () => {
      try {
        const ratio = sAmount;
        const baseAmount = reduce ? maxReduceAmount : avbalance.toNumber();
        const amt = (ratio / 100) * baseAmount;
        
        // 현재 증거금에서 변경될 금액을 더하거나 뺌
        const aftermargin = new BigNumber(item?.margin || 0)
          .plus(reduce ? -amt : amt)
          .toNumber();

        // 코인 심볼 추출 (예: BTC/USDT -> BTC)
        const coinSymbol = item?.pairs?.split('/')[0] || 'BTC';
        
        const l = await calculateLiquidPriceNew(
          coinSymbol,
          item?.price,
          item?.amount,
          aftermargin,
          item?.long_short,
          'I', // 격리 모드
          aftermargin, // 격리 모드에서는 balance와 margin이 같음
          item?.leverage
        ) || item?.liquid_price;

        setLiquid(+new BigNumber(l).toFixed(3));
      } catch (error) {
        console.error('Error calculating liquid price:', error);
        setLiquid(item?.liquid_price || 0);
      }
    };

    calculateLiquid();
  }, [
    sAmount,
    reduce,
    avbalance,
    maxReduceAmount,
    item?.margin,
    item?.pairs,
    item?.price,
    item?.amount,
    item?.long_short,
    item?.leverage,
    item?.liquid_price,
  ]);

  // Slider 값 변경 처리
  const handleSliderChange = useCallback((value) => {
    setIsDirectEditing(false);
    setSAmount(value);
    const baseAmount = reduce ? maxReduceAmount : avbalance.toNumber();
    // 최대 값이 0이면 계산된 금액도 0으로 설정
    if (baseAmount <= 0 && reduce) {
      setAmount('0');
      return;
    }
    const calculatedAmount = new BigNumber(value)
      .dividedBy(100)
      .times(baseAmount)
      .toFixed(4, BigNumber.ROUND_DOWN);
    const finalAmount = reduce ? `-${calculatedAmount}` : calculatedAmount;
    setAmount(finalAmount);
  }, [reduce, avbalance, maxReduceAmount, setAmount]);

  // 마운트 시 초기화
  useEffect(() => {
    // 초기 값 설정
    if (amount === undefined || amount === null || amount === '') {
      setAmount('0');
      setSAmount(0);
    }
    
    // 초기 모드 설정을 부모 컴포넌트에 알림
    onReduceModeChange(reduce);
    onMaxReduceAmountChange(maxReduceAmount);
  }, []);

  // 값이 변경될 때마다 부모 컴포넌트에 알림
  useEffect(() => {
    onReduceModeChange(reduce);
  }, [reduce, onReduceModeChange]);

  // 금액 유효성 검사
  const validateAmount = () => {
    if (!amount) return false;
    
    const numValue = parseFloat(amount);
    if (isNaN(numValue)) return false;
    
    // 감소 모드에서는 음수, 증가 모드에서는 양수만 허용
    if (reduce && numValue >= 0) return false;
    if (!reduce && numValue <= 0) return false;
    
    // 감소 모드에서 최대 감소 가능 금액 초과 확인
    if (reduce && Math.abs(numValue) > maxReduceAmount) return false;
    
    // 증가 모드에서 최대 증가 가능 금액 초과 확인
    if (!reduce && numValue > avbalance.toNumber()) return false;
    
    return true;
  };

  // 확인 버튼 활성화 여부
  const isConfirmDisabled = !validateAmount();

  return (
    <View style={{ padding: '16px', width: '100%' }}>
      <StyledRadioGroup
        value={reduce ? 'reduce' : 'add'}
        onChange={handleModeChange}
        buttonStyle="solid"
      >
        <Radio.Button value="reduce">{t("reduce")}</Radio.Button>
        <Radio.Button value="add">{t("add")}</Radio.Button>
      </StyledRadioGroup>

      <MaxAmountBadge $reduce={reduce}>
        <span>
          {t("maximum")} {reduce ? t('reduced') : t('increased')} {t('amount')}
        </span>
        <span>
          {(reduce ? maxReduceAmount : avbalance)?.toFixed(4)} USDT
        </span>
      </MaxAmountBadge>

      <AmountInput
        value={amount}
        type="text"
        step="0.0001"
        placeholder={t("enterAmount")}
        onChange={handleAmountChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        suffix="USDT"
        autoComplete="off"
        maxLength={20}
        disabled={reduce && maxReduceAmount <= 0}
      />

      <SliderWrapper $reduce={reduce}>
        <Slider
          marks={marks}
          defaultValue={0}
          value={sAmount || 0}
          onChange={handleSliderChange}
          min={0}
          max={100}
          style={{ width: '100%' }}
          styles={{
            rail: {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              height: 6,
              borderRadius: 3
            },
            track: {
              backgroundColor: reduce ? '#ef4444' : '#22c55e',
              height: 6,
              borderRadius: 3
            },
            handle: {
              borderColor: reduce ? '#ef4444' : '#22c55e',
              backgroundColor: reduce ? '#ef4444' : '#22c55e',
              width: 16,
              height: 16,
              marginTop: -5,
              borderRadius: '50%',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
            }
          }}
          disabled={reduce && maxReduceAmount <= 0}
        />
      </SliderWrapper>

      <InfoGrid>
        <InfoItem>
          <InfoLabel>{t('current')} {t('margin')}</InfoLabel>
          <InfoValue>{new BigNumber(item?.margin || 0).toFixed(2)} USDT</InfoValue>
        </InfoItem>

        <InfoItem>
          <InfoLabel>{t('initial')} {t('margin')}</InfoLabel>
          <InfoValue>{new BigNumber(initialMargin).toFixed(2)} USDT</InfoValue>
        </InfoItem>

        <InfoItem>
          <InfoLabel>{t('add')} {t('margin')}</InfoLabel>
          <InfoValue>{new BigNumber(additionalMargin).toFixed(2)} USDT</InfoValue>
        </InfoItem>

        <InfoItem>
          <InfoLabel>{t('marginAfterAdjustment')}</InfoLabel>
          <InfoValue>
            {new BigNumber(item?.margin || 0)
              .plus(amount || 0)
              .toFixed(2)} USDT
          </InfoValue>
        </InfoItem>

        <InfoItem style={{ gridColumn: '1 / -1' }}>
          <InfoLabel>{t('liqPriceAfterAdjustment')}</InfoLabel>
          <InfoValue style={{ color: '#f59e0b' }}>
            {liquid ? `${liquid} USDT` : t('calculating')}
          </InfoValue>
        </InfoItem>
      </InfoGrid>
    </View>
  );
};

export default React.memo(AddMargin);
